import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Register from './components/Register';
import Home from './pages/Home';
import Profile from "./pages/Profile";
import RoleSelection from "./pages/RoleSelection";
import BountyDetails from "./pages/bounty/[id]";
import BuilderOnboarding from './pages/onboarding/BuilderOnboarding';
import SponsorOnboarding from './pages/onboarding/SponsorOnboarding';
import Navbar from './components/Navbar';
import SubmitBounty from "./pages/bounty/SubmitBounty";
import ProjectDetails from "./pages/project/ProjectDetails";

type MainTab = "bounties" | "projects" | "leaderboard";

// Protected Route component for actions that require registration
const ProtectedActionRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Allow access to onboarding pages even when not registered
  const isOnboardingPage = location.pathname.startsWith('/onboarding/');
  if (!isOnboardingPage) {
    // For non-onboarding pages, check registration status
    if (!user.isRegistered || !user.hasCompletedOnboarding) {
      return <Navigate to="/role-selection" />;
    }
  }

  return <>{children}</>;
};

const AppContent = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [mainTab, setMainTab] = useState<MainTab>("bounties");

  // Update mainTab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/bounties')) {
      setMainTab('bounties');
    } else if (path.startsWith('/projects')) {
      setMainTab('projects');
    } else if (path.startsWith('/leaderboard')) {
      setMainTab('leaderboard');
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar mainTab={mainTab} setMainTab={setMainTab} />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/bounties" />} />
        <Route path="/register" element={!user ? <Register /> : <Navigate to="/bounties" />} />
        <Route
          path="/role-selection"
          element={
            user && (!user.isRegistered || !user.hasCompletedOnboarding) ? (
              <RoleSelection />
            ) : (
              <Navigate to="/bounties" />
            )
          }
        />

        {/* Public content routes */}
        <Route path="/" element={<Navigate to="/bounties" replace />} />
        <Route path="/bounties/*" element={<Home mainTab={mainTab} setMainTab={setMainTab} />} />
        <Route path="/projects/*" element={<Home mainTab={mainTab} setMainTab={setMainTab} />} />
        <Route path="/leaderboard" element={<Home mainTab={mainTab} setMainTab={setMainTab} />} />
        <Route path="/bounty/:id" element={<BountyDetails />} />
        <Route path="/project/:id" element={<ProjectDetails />} />

        {/* Protected action routes */}
        <Route path="/profile" element={<ProtectedActionRoute><Profile /></ProtectedActionRoute>} />
        <Route path="/bounty/:id/submit" element={<ProtectedActionRoute><SubmitBounty /></ProtectedActionRoute>} />
        <Route path="/onboarding/builder" element={<ProtectedActionRoute><BuilderOnboarding /></ProtectedActionRoute>} />
        <Route path="/onboarding/sponsor" element={<ProtectedActionRoute><SponsorOnboarding /></ProtectedActionRoute>} />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
